import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, AudioLines, Clapperboard, Copy, Eraser, FilePlus2, Film, FolderLock, ListPlus, ListVideo, Loader2, Magnet, MousePointer2, Pause, Play, Plus,
  ArrowLeftToLine, ArrowRightToLine, Download, Link2, Maximize2, Redo2, RefreshCw, Scissors, SkipBack, SkipForward, Slice, Trash2, Undo2, Unlink2, Volume2, X, ZoomIn, ZoomOut,
} from 'lucide-react';
import { NodeData } from '../../types';
import {
  MAX_PX_PER_SEC, MIN_GAIN_DB, MIN_PX_PER_SEC, addAsset, addTrack, canLinkClips, clipEnd, clipGainDb, createEmptyProject, deleteClips, duplicateClips, formatGainDb,
  clearInOut, formatTimecode, gainTargets, insertAsset, linkClips, projectDuration, removeAsset, rippleDeleteClips, setClipGain, setInPoint, setOutPoint, splitClip,
  unlinkClips, updateAsset, formatFrameTimecode, projectFrameRate, usableDuration,
  type EditorAsset, type InsertPlacement, type QuickEditorProject, type TrackKind,
} from '../../utils/quickEditor';
import { readVideoTiming, type VideoTiming } from '../../utils/frameRate';
import { getEditorMedia, putEditorMedia } from '../../utils/editorMediaStore';
import { isClipKept, loadEditorClip, type ClipOrigin } from '../../utils/editorClips';
import { getLocalFolderAccess } from '../../utils/localSave';
import { stableMediaKey } from '../../utils/workspaceEdit';
import { MediaLoadError, loadMediaBlob } from '../../utils/mediaLoader';
import { captureVideoFrame } from '../../utils/videoThumbnail';
import { frameOfStoredVideo, videoPosterKey } from '../../utils/videoPoster';
import { ensureVideoDuration } from '../../utils/videoTrim';
import { MediaSlider } from '../VideoPlayer';
import { EDITOR_ICON_BTN, EDITOR_MEDIA_DRAG, EDITOR_REMOVE_BTN, EditorTimeline, type EditorTimelineHandle, type EditorTool, type MediaDrag, type MediaDrop } from './quickEditor/EditorTimeline';
import { useEditorPlayback } from './quickEditor/useEditorPlayback';
import { useLocalFolderAccess } from './quickEditor/useLocalFolderAccess';
import { useMediaThumbnails, type ThumbnailRequest } from './quickEditor/useMediaThumbnails';
import { useWaveforms, type WaveformSource } from './quickEditor/useWaveforms';
import { ExportDialog } from './quickEditor/ExportDialog';

interface CanvasMedia {
  id: string;
  url: string;
  label: string;
  sourceId?: string;
  /** A short remark shown under the label. */
  note?: string;
}

interface QuickEditorNodeProps {
  data: NodeData;
  onDataChange: (newData: NodeData) => void;
  userId?: string;
  workspaceName?: string;
  ownerUserId?: string;
  isViewOnly?: boolean;
  /** Videos from connected nodes (Video and Reference nodes); offered in the media list. */
  inputVideos?: CanvasMedia[];
  /** Audio from connected Reference nodes; offered in the media list. */
  inputAudio?: CanvasMedia[];
  /**
   * 'node' is the editor inside a canvas node. 'page' fills the workspace's Edit page,
   * where inputVideos and inputAudio hold all of the workspace's media.
   */
  variant?: 'node' | 'page';
}

type MediaState =
  | { status: 'loading' }
  /** Not on this device, and not downloaded while the local folder may still have it (access pending). */
  | { status: 'waiting' }
  /** `origin` says where a canvas clip came from; files added from disk have none. */
  | { status: 'ready'; url: string; origin?: ClipOrigin; blob?: Blob }
  | { status: 'missing'; reason: string };

const makeId = (): string => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
const round3 = (value: number) => Math.round(value * 1000) / 1000;
const canvasAssetId = (kind: TrackKind, item: CanvasMedia): string => `ref-${kind}-${item.sourceId || item.id}`;
/** Addresses that only live for this session; such media is copied into the editor's own store. */
const isSessionOnly = (url: string) => /^(blob|data):/i.test(url);

const ORIGIN_LABEL: Record<ClipOrigin, string> = {
  device: 'On this device',
  folder: 'In your folder',
  download: 'Downloaded once, kept here',
};

/** One line about media that could not be added: its name once, and one full stop. */
const describeProblem = (name: string, e: unknown): string => {
  const message = ((e as { message?: string } | null)?.message || 'could not be added').trim().replace(/\.+$/, '');
  return message.includes(name) ? `${message}.` : `${name}: ${message}.`;
};

const kindOfFile = (file: File): TrackKind | null => {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (/\.(mp4|mov|webm|m4v)$/i.test(file.name)) return 'video';
  if (/\.(mp3|wav|m4a|ogg)$/i.test(file.name)) return 'audio';
  return null;
};

/** Read a media file's length (and frame size for video) from its metadata. */
/** A video's frame rate, and how long its picture runs when the file's sound runs on past it. */
const timingFields = (timing: VideoTiming | null | undefined): Partial<EditorAsset> =>
  (timing ? { frameRate: timing.frameRate, ...(timing.durationSec ? { videoDurationSec: timing.durationSec } : {}) } : {});

const probeMedia = (url: string, kind: TrackKind): Promise<{ durationSec: number; width?: number; height?: number }> =>
  new Promise((resolve, reject) => {
    const el = document.createElement(kind === 'video' ? 'video' : 'audio');
    el.preload = 'metadata';
    el.muted = true;
    el.onloadedmetadata = () => {
      void ensureVideoDuration(el as HTMLVideoElement).then((durationSec) => {
        const video = el as HTMLVideoElement;
        resolve({ durationSec, width: video.videoWidth || undefined, height: video.videoHeight || undefined });
      });
    };
    el.onerror = () => reject(new Error('it could not be read as media'));
    el.src = url;
  });

const ToolButton: React.FC<{
  label: string;
  shortcut?: string;
  /** For a tool or a setting that is on or off: whether it is on. Left out for a plain action. */
  pressed?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ label, shortcut, pressed, disabled, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    aria-pressed={pressed}
    title={shortcut ? `${label} (${shortcut})` : label}
    className={EDITOR_ICON_BTN}
  >
    {children}
  </button>
);

/**
 * Text buttons on the editor's ink surface. The main action is the cream button made for
 * dark surfaces; everything else is the quiet outlined one.
 */
const BTN_MAIN = 'ns-btn ns-btn-xs ns-btn-cream ns-btn-flat shrink-0 focus-visible:outline-lavender';
const BTN_QUIET = 'ns-btn ns-btn-xs ns-btn-flat shrink-0 ns-btn-ghost-dark focus-visible:outline-lavender';

const Divider: React.FC = () => <span className="w-px h-5 bg-white/10 mx-1 shrink-0" aria-hidden="true" />;

/**
 * Quick Editor: a small multi-track timeline for checking whether an edit flows.
 * Media is read from this device: files added here are kept in the browser's local
 * store, and clips from the canvas come from the editor's own copy, this session's
 * memory or the local auto-save folder, and are downloaded only when none of these has
 * them (once; the download is kept). Video clips carry a linked audio clip, and clips
 * can be moved, trimmed from either side and cut. The edit itself is saved with the node.
 */
export const QuickEditorNode: React.FC<QuickEditorNodeProps> = ({
  data, onDataChange, userId, workspaceName, ownerUserId, isViewOnly = false, inputVideos = [], inputAudio = [], variant = 'node',
}) => {
  const isPage = variant === 'page';
  const saved = useMemo(() => data.quickEditor ?? createEmptyProject(), [data.quickEditor]);
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const dataRef = useRef(data);
  dataRef.current = data;

  const [draft, setDraft] = useState<QuickEditorProject | null>(null);
  const project = draft ?? saved;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [tool, setTool] = useState<EditorTool>('select');
  const [exporting, setExporting] = useState(false);
  const [media, setMedia] = useState<Record<string, MediaState>>({});
  const [busy, setBusy] = useState(false);
  // The workspace clip being loaded now (with download progress) and the ones waiting their turn
  const [loadingClip, setLoadingClip] = useState<{ id: string; received: number; total: number | null } | null>(null);
  const [waitingClips, setWaitingClips] = useState<string[]>([]);
  // Media dragged from the list onto the timeline: what it is (for the drop preview) and where it comes from
  const [mediaDrag, setMediaDrag] = useState<(MediaDrag & { source: { assetId: string } | { item: CanvasMedia; kind: TrackKind } }) | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [, bumpHistory] = useState(0);
  // Bumped to load clips again whose load ended just after downloading became allowed
  const [retryRound, setRetryRound] = useState(0);

  // ── Where media may come from ─────────────────────────────────────────────
  // While the browser still has to allow the local folder again (after a restart), clips
  // are not downloaded: they may be on disk. The user either allows access or chooses to
  // download. Without a folder, a clip that is not on this device is downloaded once.
  const { access, request: requestFolderAccess } = useLocalFolderAccess(userId);
  const [downloadApproved, setDownloadApproved] = useState(false);
  const folderLocked = access?.state === 'prompt';
  const allowDownload = access !== null && (!folderLocked || downloadApproved);
  const allowDownloadRef = useRef(allowDownload);
  allowDownloadRef.current = allowDownload;

  const past = useRef<QuickEditorProject[]>([]);
  const future = useRef<QuickEditorProject[]>([]);
  const requested = useRef(new Set<string>());
  const objectUrls = useRef<string[]>([]);
  const mounted = useRef(true);
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const addInputRef = useRef<HTMLInputElement>(null);
  const relinkInputRef = useRef<HTMLInputElement>(null);
  const relinkTarget = useRef<string | null>(null);
  const playheadMover = useRef<((timeSec: number, playing: boolean) => void) | null>(null);
  const adding = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const urls = objectUrls.current;
    return () => {
      mounted.current = false;
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, []);

  // The Edit page is all editor, so its shortcuts work as soon as it opens.
  useEffect(() => {
    if (isPage) rootRef.current?.focus({ preventScroll: true });
  }, [isPage]);

  // ── The edit: commit, undo, redo ──────────────────────────────────────────
  const write = useCallback((next: QuickEditorProject) => {
    savedRef.current = next;
    onDataChange({ ...dataRef.current, quickEditor: next });
  }, [onDataChange]);

  const commit = useCallback((next: QuickEditorProject, record = true) => {
    setDraft(null);
    if (next === savedRef.current) return;
    if (record) {
      past.current.push(savedRef.current);
      if (past.current.length > 100) past.current.shift();
      future.current = [];
    }
    write(next);
    bumpHistory(n => n + 1);
  }, [write]);

  const undo = useCallback(() => {
    const previous = past.current.pop();
    if (!previous) return;
    future.current.push(savedRef.current);
    setDraft(null);
    write(previous);
    bumpHistory(n => n + 1);
  }, [write]);

  const redo = useCallback(() => {
    const next = future.current.pop();
    if (!next) return;
    past.current.push(savedRef.current);
    setDraft(null);
    write(next);
    bumpHistory(n => n + 1);
  }, [write]);

  // ── Media: local copies for instant scrubbing ─────────────────────────────
  const mediaRef = useRef(media);
  mediaRef.current = media;

  // Waveforms for the media on audio tracks, made from the bytes already here, to see where to cut
  const waveSources = useMemo(() => {
    const result: Record<string, WaveformSource> = {};
    for (const [id, state] of Object.entries(media)) if (state.status === 'ready') result[id] = { blob: state.blob, url: state.url };
    return result;
  }, [media]);
  // The bytes of each loaded asset, for exporting
  const mediaBlobs = useMemo(() => {
    const result: Record<string, Blob> = {};
    for (const [id, state] of Object.entries(media)) if (state.status === 'ready' && state.blob) result[id] = state.blob;
    return result;
  }, [media]);
  const onAudioTracks = useMemo(() => {
    const audioTracks = new Set(saved.tracks.filter(track => track.kind === 'audio').map(track => track.id));
    return new Set(saved.clips.filter(clip => audioTracks.has(clip.trackId)).map(clip => clip.assetId));
  }, [saved.tracks, saved.clips]);
  const { waves: waveforms, status: waveformStatus } = useWaveforms(saved.assets, waveSources, onAudioTracks);
  const lastAccessState = useRef<string | null>(null);

  useEffect(() => {
    if (access === null) return; // first learn whether the local folder can be read
    if (access.state === 'granted' && lastAccessState.current !== 'granted') {
      // The folder just became readable: canvas clips that failed may be on disk
      for (const asset of savedRef.current.assets) {
        if (asset.source.type === 'ref' && mediaRef.current[asset.id]?.status === 'missing') requested.current.delete(asset.id);
      }
    }
    lastAccessState.current = access.state;

    for (const asset of saved.assets) {
      if (requested.current.has(asset.id)) continue;
      requested.current.add(asset.id);
      setMedia(prev => ({ ...prev, [asset.id]: { status: 'loading' } }));
      void (async () => {
        try {
          let blob: Blob;
          let origin: ClipOrigin | undefined;
          if (asset.source.type === 'local') {
            const kept = await getEditorMedia(asset.source.key);
            if (!kept) throw new Error('This file is not on this device. Relink it to keep editing.');
            blob = kept;
          } else {
            const clip = await loadEditorClip(asset.source.ref, {
              userId, ownerUserId, workspaceName, label: asset.name, defaultMimeType: asset.kind === 'video' ? 'video/mp4' : 'audio/mpeg',
              allowDownload,
            });
            blob = clip.blob;
            origin = clip.origin;
          }
          // A download that finishes after the editor closed is still kept for next time; it just is not shown.
          if (!mounted.current) return;
          const url = URL.createObjectURL(blob);
          objectUrls.current.push(url);
          setMedia(prev => ({ ...prev, [asset.id]: { status: 'ready', url, origin, blob } }));
        } catch (e: any) {
          if (!mounted.current) return;
          if (e instanceof MediaLoadError && e.code === 'not-local') {
            requested.current.delete(asset.id);
            if (allowDownloadRef.current) {
              // Downloading was allowed while this load ran: load it again right away
              setRetryRound(n => n + 1);
              return;
            }
            // Loaded again once the folder can be read or downloading is approved
            setMedia(prev => ({ ...prev, [asset.id]: { status: 'waiting' } }));
            return;
          }
          setMedia(prev => ({ ...prev, [asset.id]: { status: 'missing', reason: e?.message || 'Media unavailable' } }));
        }
      })();
    }
  }, [saved.assets, userId, ownerUserId, workspaceName, access, allowDownload, retryRound]);

  // ── Frame rate: the timeline runs at the rate of its video ────────────────
  // Clips added before rates were kept have theirs read once their bytes are here, and it is
  // kept with the edit; until then (and in a view-only edit) it is used from here.
  const rateRequested = useRef(new Set<string>());
  const [readRates, setReadRates] = useState<Record<string, VideoTiming>>({});
  useEffect(() => {
    for (const asset of saved.assets) {
      if (asset.kind !== 'video' || asset.frameRate || rateRequested.current.has(asset.id)) continue;
      const state = media[asset.id];
      if (state?.status !== 'ready' || !state.blob) continue;
      rateRequested.current.add(asset.id);
      void readVideoTiming(state.blob).then((timing) => {
        if (timing && mounted.current) setReadRates(prev => ({ ...prev, [asset.id]: timing }));
      });
    }
  }, [saved.assets, media]);
  useEffect(() => {
    if (isViewOnly) return;
    const missing = savedRef.current.assets.filter(asset => !asset.frameRate && readRates[asset.id]);
    if (missing.length === 0) return;
    let next = savedRef.current;
    for (const asset of missing) next = updateAsset(next, asset.id, timingFields(readRates[asset.id]));
    write(next);
  }, [saved.assets, readRates, isViewOnly, write]);
  const frameRate = useMemo(() => projectFrameRate(project.assets.some(asset => !asset.frameRate && readRates[asset.id])
    ? { ...project, assets: project.assets.map(asset => (asset.frameRate || !readRates[asset.id] ? asset : { ...asset, ...timingFields(readRates[asset.id]) })) }
    : project), [project, readRates]);

  const urls = useMemo(() => {
    const out: Record<string, string> = {};
    for (const [assetId, state] of Object.entries(media)) if (state.status === 'ready') out[assetId] = state.url;
    return out;
  }, [media]);
  const missingAssetIds = useMemo(
    () => new Set(Object.entries(media).filter(([, state]) => state.status === 'missing').map(([assetId]) => assetId)),
    [media],
  );

  const playback = useEditorPlayback({
    project,
    urls,
    canvasRef,
    frameRate,
    onFrame: (timeSec, playing) => playheadMover.current?.(timeSec, playing),
  });
  const registerPlayhead = useCallback((move: ((timeSec: number, playing: boolean) => void) | null) => { playheadMover.current = move; }, []);

  const duration = projectDuration(project);

  // Drop selections that no longer exist (after an undo, a delete or a sync).
  useEffect(() => {
    setSelectedIds(prev => {
      const ids = new Set(saved.clips.map(clip => clip.id));
      const kept = prev.filter(id => ids.has(id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [saved.clips]);

  // ── Adding media ──────────────────────────────────────────────────────────
  const describeAsset = async (id: string, name: string, kind: TrackKind, url: string, source: EditorAsset['source'], extra: Partial<EditorAsset> = {}, blob?: Blob): Promise<EditorAsset> => {
    const [info, timing] = await Promise.all([
      probeMedia(url, kind),
      kind === 'video' && blob ? readVideoTiming(blob) : Promise.resolve(null),
    ]);
    if (!info.durationSec || info.durationSec <= 0) throw new Error('its length could not be read');
    if (timing) rateRequested.current.add(id);
    return {
      id, name, kind,
      durationSec: round3(info.durationSec),
      hasAudio: kind === 'video',
      ...(info.width && info.height ? { width: info.width, height: info.height } : {}),
      ...timingFields(timing),
      ...extra,
      source,
    };
  };

  /** Files dropped while another add was running; added as soon as it finishes, where they were dropped. */
  const pendingFiles = useRef<{ files: File[]; drop?: MediaDrop }[]>([]);
  const addPendingFiles = () => {
    if (!mounted.current || pendingFiles.current.length === 0) return;
    const next = pendingFiles.current.shift()!;
    setTimeout(() => { void addFiles(next.files, next.drop); }, 0);
  };

  /** Add files from this device: at the end of the timeline, or where they were dropped on it. */
  const addFiles = async (files: FileList | File[] | null, drop?: MediaDrop) => {
    if (isViewOnly || !files) return;
    if (adding.current) {
      // Copied now: a FileList from a drop or an input does not outlive the event
      pendingFiles.current.push({ files: Array.from(files), drop });
      return;
    }
    adding.current = true;
    setBusy(true);
    const problems: string[] = [];
    const described: EditorAsset[] = [];
    try {
      for (const file of Array.from(files)) {
        const kind = kindOfFile(file);
        if (!kind) { problems.push(`${file.name} is not a video or audio file.`); continue; }
        const id = makeId();
        const url = URL.createObjectURL(file);
        objectUrls.current.push(url);
        try {
          const asset = await describeAsset(id, file.name, kind, url, { type: 'local', key: id }, {
            sizeBytes: file.size, ...(file.type ? { mimeType: file.type } : {}),
          }, file);
          if (!mounted.current) return;
          requested.current.add(id);
          setMedia(prev => ({ ...prev, [id]: { status: 'ready', url, blob: file } }));
          putEditorMedia(id, file).catch(() => {
            if (mounted.current) setNotice('This browser is out of space for local media; files added now will need to be added again after a reload.');
          });
          described.push(asset);
        } catch (e: any) {
          problems.push(describeProblem(file.name, e));
        }
      }
      // Applied to the edit as it is now, so changes made while the files were read are kept.
      // Dropped files go where they were dropped, one after the other on that track; others at the end.
      if (described.length > 0) {
        let next = savedRef.current;
        let at = drop ? drop.timeSec : projectDuration(next);
        let placement: InsertPlacement = drop ? { trackId: drop.trackId, newTrack: drop.newTrack } : {};
        for (const asset of described) {
          const before = next.clips.length;
          next = insertAsset(addAsset(next, asset), asset.id, at, makeId, placement);
          if (!drop) { at = projectDuration(next); continue; }
          // The next file follows this one on the track it landed on (its own clip comes first, its sound after)
          const landed = next.clips[before];
          at = landed ? clipEnd(landed) : at + usableDuration(asset);
          placement = landed ? { trackId: landed.trackId } : placement;
        }
        commit(next);
      }
    } finally {
      adding.current = false;
      if (mounted.current) {
        setBusy(false);
        if (problems.length > 0) setNotice(problems.join(' '));
      }
      addPendingFiles();
    }
  };

  /**
   * Ask for the local folder when adding needs it: the browser has not allowed it yet and
   * some clip asked for may be on disk (not one this browser keeps, nor session-only
   * media). Called at the start of a click, so the browser can show its prompt; the
   * check of kept clips has a short time limit so the click is still fresh when it asks.
   */
  const askForFolderIfLocked = async (entries: { item: CanvasMedia }[]) => {
    if (allowDownloadRef.current) return;
    // Access may not be known yet (the editor just opened); find out, still within the click.
    const current = access ?? await getLocalFolderAccess(userId).catch(() => null);
    if (current?.state !== 'prompt') return;
    const allKept = Promise.all(entries.map(({ item }) => (isSessionOnly(item.url) ? true : isClipKept(item.url, userId))))
      .then(kept => kept.every(Boolean));
    const answeredInTime = await Promise.race([allKept, new Promise<boolean>(resolve => setTimeout(() => resolve(false), 300))]);
    if (answeredInTime) return;
    await requestFolderAccess();
  };

  /**
   * Load media that lives on the canvas and append it to the timeline, one item at a time.
   * The clips asked for are downloaded when they are not on this device, even if folder
   * access was refused; other clips keep waiting for the user's choice in the banner.
   */
  /** Clips asked for while another was loading; they are added in turn, in the order asked. */
  const waitingAdds = useRef<{ entries: { item: CanvasMedia; kind: TrackKind }[]; drop?: MediaDrop }[]>([]);

  const addFromCanvas = async (entries: { item: CanvasMedia; kind: TrackKind }[], drop?: MediaDrop) => {
    if (isViewOnly || entries.length === 0) return;
    if (adding.current) {
      // One add at a time, so clips land in the order asked for; this one waits its turn
      const waiting = new Set(waitingClips);
      const fresh = entries.filter(({ item, kind }) => !waiting.has(canvasAssetId(kind, item)) && loadingClip?.id !== canvasAssetId(kind, item));
      if (fresh.length === 0) return;
      waitingAdds.current.push({ entries: fresh, drop });
      setWaitingClips(prev => [...prev, ...fresh.map(({ item, kind }) => canvasAssetId(kind, item))]);
      return;
    }
    // Locked before the permission bubble can open, so a second click cannot start a second add
    adding.current = true;
    setBusy(true);
    const problems: string[] = [];
    try {
      await askForFolderIfLocked(entries);
      for (const { item, kind } of entries) {
        if (!mounted.current) return;
        const id = canvasAssetId(kind, item);
        setWaitingClips(prev => prev.filter(waiting => waiting !== id));
        setLoadingClip({ id, received: 0, total: null });
        try {
          let asset: EditorAsset | null = null;
          if (!savedRef.current.assets.some(existing => existing.id === id)) {
            const defaultMimeType = kind === 'video' ? 'video/mp4' : 'audio/mpeg';
            // A blob or data URL only lives for this session, so such media is kept in the editor's store instead.
            const sessionOnly = isSessionOnly(item.url);
            const { blob, origin } = sessionOnly
              ? { ...(await loadMediaBlob(item.url, { label: item.label, defaultMimeType })), origin: 'device' as ClipOrigin }
              : await loadEditorClip(item.url, {
                  userId, ownerUserId, workspaceName, label: item.label, defaultMimeType, allowDownload: true,
                  onDownloadProgress: (received, total) => { if (mounted.current) setLoadingClip({ id, received, total }); },
                });
            if (!mounted.current) return;
            const url = URL.createObjectURL(blob);
            asset = await describeAsset(id, item.label, kind, url, sessionOnly ? { type: 'local', key: id } : { type: 'ref', ref: item.url }, { sizeBytes: blob.size }, blob)
              .catch((e) => { URL.revokeObjectURL(url); throw e; });
            if (!mounted.current) { URL.revokeObjectURL(url); return; }
            objectUrls.current.push(url);
            if (sessionOnly) putEditorMedia(id, blob).catch(() => undefined);
            requested.current.add(id);
            setMedia(prev => ({ ...prev, [id]: { status: 'ready', url, origin: sessionOnly ? undefined : origin, blob } }));
          }
          // Built on the latest edit each time, so changes made while media loads are kept.
          // Dropped media goes where it was dropped; otherwise it is added at the end.
          const base = asset ? addAsset(savedRef.current, asset) : savedRef.current;
          commit(drop
            ? insertAsset(base, id, drop.timeSec, makeId, { trackId: drop.trackId, newTrack: drop.newTrack })
            : insertAsset(base, id, projectDuration(base), makeId));
        } catch (e: any) {
          problems.push(describeProblem(item.label, e));
        }
      }
    } finally {
      adding.current = false;
      if (mounted.current) {
        setLoadingClip(null);
        setBusy(false);
        if (problems.length > 0) setNotice(problems.join(' '));
      }
      addPendingFiles();
      // Then the clips that were asked for meanwhile
      const next = waitingAdds.current.shift();
      if (next && mounted.current) setTimeout(() => { void addFromCanvas(next.entries, next.drop); }, 0);
    }
  };

  /** Media from the list dropped on a track, or on the strip for a new layer. */
  const dropMedia = (drop: MediaDrop) => {
    const dragged = mediaDrag;
    setMediaDrag(null);
    if (!dragged || isViewOnly) return;
    if ('assetId' in dragged.source) {
      commit(insertAsset(savedRef.current, dragged.source.assetId, drop.timeSec, makeId, { trackId: drop.trackId, newTrack: drop.newTrack }));
    } else {
      void addFromCanvas([dragged.source], drop);
    }
  };
  /** Drag handlers for a list item; the item's length sizes the drop preview (a guess until loaded). */
  const dragFromList = (drag: NonNullable<typeof mediaDrag>) => (isViewOnly ? {} : {
    draggable: true,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.setData(EDITOR_MEDIA_DRAG, '1');
      e.dataTransfer.effectAllowed = 'copy';
      setMediaDrag(drag);
    },
    onDragEnd: () => setMediaDrag(null),
  });

  const relink = async (file: File | undefined) => {
    const assetId = relinkTarget.current;
    relinkTarget.current = null;
    const asset = savedRef.current.assets.find(a => a.id === assetId);
    if (!file || !asset) return;
    if (kindOfFile(file) !== asset.kind) { setNotice(`Choose ${asset.kind === 'video' ? 'a video' : 'an audio'} file to relink ${asset.name}.`); return; }
    const url = URL.createObjectURL(file);
    objectUrls.current.push(url);
    try {
      const [info, timing] = await Promise.all([probeMedia(url, asset.kind), asset.kind === 'video' ? readVideoTiming(file) : Promise.resolve(null)]);
      setMedia(prev => ({ ...prev, [asset.id]: { status: 'ready', url, blob: file } }));
      putEditorMedia(asset.id, file).catch(() => undefined);
      commit(updateAsset(savedRef.current, asset.id, {
        durationSec: round3(info.durationSec), sizeBytes: file.size, source: { type: 'local', key: asset.id }, ...timingFields(timing),
      }), false);
    } catch (e: any) {
      setNotice(`${file.name}: ${e?.message || 'could not be read'}.`);
    }
  };

  const dropAsset = (asset: EditorAsset) => {
    if (isViewOnly) return;
    // The stored bytes of a file from this device are kept: an undo, a pasted copy of this editor or a
    // duplicated workspace may still use them.
    requested.current.delete(asset.id);
    setMedia(prev => {
      const { [asset.id]: _gone, ...rest } = prev;
      return rest;
    });
    commit(removeAsset(savedRef.current, asset.id));
  };

  // ── Editing actions ───────────────────────────────────────────────────────
  const splitAtPlayhead = useCallback(() => {
    if (isViewOnly) return;
    const at = playback.getTime();
    const current = savedRef.current;
    const candidates = selectedIds.length > 0 ? selectedIds : current.clips.map(clip => clip.id);
    let next = current;
    for (const clipId of candidates) {
      const clip = next.clips.find(c => c.id === clipId);
      if (clip && at > clip.startSec && at < clipEnd(clip)) next = splitClip(next, clipId, at, makeId);
    }
    commit(next);
  }, [commit, isViewOnly, playback, selectedIds]);

  const deleteSelection = useCallback(() => {
    if (isViewOnly || selectedIds.length === 0) return;
    commit(deleteClips(savedRef.current, selectedIds));
    setSelectedIds([]);
  }, [commit, isViewOnly, selectedIds]);

  /** Delete the selected clips and close the gaps they leave (Shift+Delete) */
  const rippleDeleteSelection = useCallback(() => {
    if (isViewOnly || selectedIds.length === 0) return;
    commit(rippleDeleteClips(savedRef.current, selectedIds));
    setSelectedIds([]);
  }, [commit, isViewOnly, selectedIds]);

  /** Copy the selected clips to right after themselves, and select the copies (Ctrl+D) */
  const duplicateSelection = useCallback(() => {
    if (isViewOnly || selectedIds.length === 0) return;
    const { project: next, copies } = duplicateClips(savedRef.current, selectedIds, makeId);
    if (copies.length === 0) return;
    commit(next);
    setSelectedIds(copies);
  }, [commit, isViewOnly, selectedIds]);

  const timelineRef = useRef<EditorTimelineHandle>(null);
  /** Zoom so the whole edit is in view (F) */
  const fitToView = useCallback(() => timelineRef.current?.fitToView(), []);

  // Link and unlink: two or more separate clips (or groups) can be linked; a linked selection can be unlinked
  const selectionLinkable = canLinkClips(saved, selectedIds);
  const toggleLink = useCallback(() => {
    if (isViewOnly) return;
    const current = savedRef.current;
    if (canLinkClips(current, selectedIds)) commit(linkClips(current, selectedIds, makeId));
    else commit(selectedIds.reduce((next, clipId) => unlinkClips(next, clipId), current));
  }, [commit, isViewOnly, selectedIds]);

  // The volume of the selected clips' sound, in decibels; a drag on the slider is one undo step
  const gainSelection = gainTargets(project, selectedIds);
  const selectionGainDb = gainSelection.length > 0 ? clipGainDb(gainSelection[0]) : 0;
  const gainDrag = useRef<{ base: QuickEditorProject; last: QuickEditorProject } | null>(null);
  const setSelectionGain = (db: number) => {
    const drag = gainDrag.current;
    if (!drag) { commit(setClipGain(savedRef.current, selectedIds, db)); return; }
    drag.last = setClipGain(drag.base, selectedIds, db);
    setDraft(drag.last);
  };

  const setZoom = useCallback((pxPerSec: number) => {
    commit({ ...savedRef.current, pxPerSec: Math.min(MAX_PX_PER_SEC, Math.max(MIN_PX_PER_SEC, pxPerSec)) }, false);
  }, [commit]);

  const selectionLinked = selectedIds.some(id => saved.clips.find(clip => clip.id === id)?.linkId);
  const linkAction: 'link' | 'unlink' | null = isViewOnly ? null : selectionLinkable ? 'link' : selectionLinked ? 'unlink' : null;

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
    // The export window has the keyboard while it is open
    if (exporting) return;
    const mod = e.ctrlKey || e.metaKey;
    const key = e.key.toLowerCase();
    let handled = true;
    if (key === ' ') playback.toggle();
    else if (mod && key === 'z' && !e.shiftKey) undo();
    else if (mod && (key === 'y' || (key === 'z' && e.shiftKey))) redo();
    else if (mod && key === 'a') setSelectedIds(savedRef.current.clips.map(clip => clip.id));
    else if (mod && key === 'd') duplicateSelection();
    else if (mod) handled = false;
    else if (key === 'escape' && selectedIds.length > 0) setSelectedIds([]);
    else if (key === 's') splitAtPlayhead();
    else if ((key === 'delete' || key === 'backspace') && e.shiftKey) rippleDeleteSelection();
    else if (key === 'delete' || key === 'backspace') deleteSelection();
    else if (key === 'f') fitToView();
    else if (key === 'v') setTool('select');
    else if (key === 'c') setTool('razor');
    else if (key === 'l') toggleLink();
    else if (key === 'i' && !isViewOnly) commit(setInPoint(savedRef.current, playback.getTime()));
    else if (key === 'o' && !isViewOnly) commit(setOutPoint(savedRef.current, playback.getTime()));
    else if (key === 'x' && e.altKey && !isViewOnly) commit(clearInOut(savedRef.current));
    else if (key === 'arrowleft') playback.seek(e.shiftKey ? playback.getTime() - 1 : (Math.round(playback.getTime() * frameRate) - 1) / frameRate);
    else if (key === 'arrowright') playback.seek(e.shiftKey ? playback.getTime() + 1 : (Math.round(playback.getTime() * frameRate) + 1) / frameRate);
    else if (key === 'home') playback.seek(0);
    else if (key === 'end') playback.seek(duration);
    else handled = false;
    if (handled) {
      e.preventDefault();
      // Keep the app's own shortcuts (delete node, paste, pan) out of the way while editing.
      e.stopPropagation();
    }
  };

  const canvasItems = [
    ...inputVideos.map(item => ({ item, kind: 'video' as TrackKind })),
    ...inputAudio.map(item => ({ item, kind: 'audio' as TrackKind })),
  ].filter(({ item, kind }) => !saved.assets.some(asset => asset.id === canvasAssetId(kind, item)));

  // ── Thumbnails, so a video can be told from its neighbours in the list ────
  /** A frame of a video that lives on the canvas: from this device first, the cloud only once allowed. */
  const frameFromCanvas = (item: CanvasMedia): Promise<Blob> =>
    frameOfStoredVideo(item.url, { userId, ownerUserId, workspaceName, label: item.label, allowDownload: allowDownloadRef.current });

  /** Pictures are keyed by the media itself where it has a lasting key, so a node that gets a new take gets a new picture. */
  const poolThumb = (item: CanvasMedia): { key: string; keep: boolean } => videoPosterKey(item.url, canvasAssetId('video', item));
  /** An asset's picture has its own key, made from its local copy, so it never waits on the list item's picture. */
  const assetThumb = (asset: EditorAsset): { key: string; keep: boolean } => {
    if (asset.source.type === 'ref') {
      const stable = isSessionOnly(asset.source.ref) ? null : stableMediaKey(asset.source.ref);
      if (stable) return { key: `asset:${stable}`, keep: true };
    } else if (!asset.id.startsWith('ref-')) {
      return { key: `asset:${asset.id}`, keep: true }; // a file from this device: its id is its own
    }
    // Session-only canvas media is stored under a node's id, which a new take reuses
    return { key: `asset:${asset.id}:${asset.sizeBytes ?? ''}:${asset.durationSec}`, keep: false };
  };
  // Nothing is fetched before it is known whether the local folder can be read.
  const thumbnailRequests: ThumbnailRequest[] = access === null ? [] : [
    ...saved.assets.filter(asset => asset.kind === 'video' && urls[asset.id]).map(asset => ({
      ...assetThumb(asset), make: () => captureVideoFrame(urls[asset.id]),
    })),
    ...canvasItems.filter(({ kind }) => kind === 'video').map(({ item }) => ({
      ...poolThumb(item), make: () => frameFromCanvas(item),
    })),
  ];
  // Pictures that could not be made yet are tried again when the folder opens up or downloading is allowed.
  const thumbnails = useMediaThumbnails(thumbnailRequests, `${access?.state}|${allowDownload}`);
  const thumbBox = `${isPage ? 'w-20 h-[45px]' : 'w-12 h-[27px]'} shrink-0 grid place-items-center rounded-md overflow-hidden ring-1 ring-white/10`;
  // A name in the media list. The names are prompts and file names, so on the page a name gets two
  // lines (and breaks inside a long file name); in the node's narrow column it keeps to one line,
  // one step smaller. Either way the whole name is in the row's title.
  const rowName = `font-semibold text-cream ${isPage ? 'line-clamp-2 break-words text-[13px] leading-tight' : 'block truncate text-xs'}`;

  // The folder prompt shows only while something from the canvas is waiting for it.
  const showFolderPrompt = folderLocked && !downloadApproved && (
    Object.values(media).some(state => state.status === 'waiting')
    || canvasItems.some(({ item, kind }) => kind === 'video' && !isSessionOnly(item.url) && !thumbnails[poolThumb(item).key])
  );

  const anySelected = selectedIds.length > 0 && !isViewOnly;

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      className={`node-scroll-trap no-drag relative w-full h-full flex flex-col bg-ink text-cream overflow-hidden ${isPage ? '' : 'rounded-b-2xl'}`}
      // The whole editor is one keyboard scope; a ring around it would only show as a stray line at its clipped top edge.
      style={{ outline: 'none' }}
      onKeyDown={onKeyDown}
      onPointerDown={() => rootRef.current?.focus({ preventScroll: true })}
      // Files dropped anywhere but on the timeline go to the end of it (the timeline takes its own drops, where they fall)
      onDragOver={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault(); }}
      onDrop={(e) => { if (e.dataTransfer.files.length > 0) { e.preventDefault(); void addFiles(e.dataTransfer.files); } }}
      data-quick-editor
    >
      <input ref={addInputRef} type="file" multiple accept="video/*,audio/*,.mp4,.mov,.webm,.m4v,.mp3,.wav,.m4a" className="hidden" onChange={(e) => { void addFiles(e.target.files); e.target.value = ''; }} />
      <input ref={relinkInputRef} type="file" accept="video/*,audio/*" className="hidden" onChange={(e) => { void relink(e.target.files?.[0]); e.target.value = ''; }} />

      {/* The top bar: what concerns the whole edit. Media in, undo and redo, the edit out. */}
      <div className="flex items-center gap-0.5 px-2 h-11 shrink-0 border-b border-white/10" data-editor-topbar>
        <button
          type="button"
          onClick={() => addInputRef.current?.click()}
          disabled={isViewOnly}
          // Files chosen while media is being added wait their turn
          title={busy ? 'Adding media; files chosen now are added next' : 'Add videos or audio from this device'}
          className={`${BTN_MAIN} h-8 px-2.5`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <FilePlus2 size={14} />} Add media
        </button>
        <Divider />
        <ToolButton label="Undo" shortcut="Ctrl+Z" onClick={undo} disabled={isViewOnly || past.current.length === 0}><Undo2 /></ToolButton>
        <ToolButton label="Redo" shortcut="Ctrl+Shift+Z" onClick={redo} disabled={isViewOnly || future.current.length === 0}><Redo2 /></ToolButton>
        {isPage && (
          <span className="ns-t-meta ml-3 truncate hidden md:inline" title="Drag media from the list, or files from this device, onto the timeline">
            Drag media onto the timeline, or files from this device.
          </span>
        )}
        <div className="ml-auto flex items-center gap-1 pl-2 shrink-0">
          <button
            type="button"
            onClick={() => { playback.pause(); setExporting(true); }}
            disabled={duration <= 0}
            className={`${BTN_MAIN} h-8 px-2.5`}
            title="Export the edit as a video file"
            data-editor-export
          >
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* What needs saying sits in a strip of its own under the top bar */}
      {(notice || (showFolderPrompt && access?.state === 'prompt')) && (
        <div className="flex flex-col gap-2 p-2 shrink-0 border-b border-white/10">
          {notice && (
            <div className="ns-note" data-tone="warn">
              <AlertTriangle size={14} aria-hidden="true" />
              <span className="flex-1">{notice}</span>
              <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="ns-icon-btn ns-icon-btn-xs -my-1 -mr-1 text-current"><X /></button>
            </div>
          )}

          {showFolderPrompt && access?.state === 'prompt' && (
            <div className="ns-note flex-wrap items-center gap-x-3 gap-y-1.5" data-tone="lavender" data-folder-access>
              <FolderLock size={14} aria-hidden="true" />
              <span className="flex-1 min-w-[200px]">
                <span className="font-semibold">Allow access to your folder {access.name}</span>
                {' '}to load videos from your drive. The browser asks again after a restart; until then nothing is downloaded.
              </span>
              <span className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => void requestFolderAccess()}
                  className="ns-btn ns-btn-xs ns-btn-primary ns-btn-flat"
                >
                  Allow access
                </button>
                <button
                  type="button"
                  onClick={() => setDownloadApproved(true)}
                  className="ns-btn ns-btn-xs ns-btn-ghost ns-btn-flat border-[color:var(--tone-line)]"
                  title="Download the clips that are not kept in this browser; each is kept after its first download"
                >
                  Download instead
                </button>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Media list, and the monitor with its transport under it */}
      <div className="flex-1 min-h-[120px] flex">
        <div className={`${isPage ? 'w-80' : 'w-56'} shrink-0 border-r border-white/10 flex flex-col min-h-0`} data-editor-media>
          <div className="ns-t-label flex items-center gap-1.5 px-3 h-8 shrink-0">
            <ListVideo size={12} aria-hidden="true" /> Media
          </div>
          <ul className="node-scrollable-content ns-scroll-dark flex-1 min-h-0 overflow-y-auto px-1.5 pb-2 flex flex-col gap-0.5">
            {saved.assets.length === 0 && canvasItems.length === 0 && (
              <li className="px-2 py-3 text-xs text-ink-faint">
                {isPage
                  ? 'Videos generated in this workspace show up here. You can also add videos or audio from this device.'
                  : 'Add videos or audio from this device, or connect a Video or Reference node.'}
              </li>
            )}
            {saved.assets.map((asset) => {
              const state = media[asset.id];
              const isMissing = state?.status === 'missing';
              const isWaiting = state?.status === 'waiting';
              const thumbnail = asset.kind === 'video' && !isMissing ? thumbnails[assetThumb(asset).key] : undefined;
              const where = state?.status === 'ready' && state.origin ? ORIGIN_LABEL[state.origin]
                : asset.source.type === 'local' ? 'On this device'
                : isWaiting ? 'Waiting for folder access'
                : 'Loading';
              const about = isMissing ? 'Media missing' : `${formatTimecode(usableDuration(asset))} · ${where}`;
              return (
                <li
                  key={asset.id}
                  className={`group flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-white/5 ${isViewOnly ? '' : 'cursor-grab active:cursor-grabbing'}`}
                  data-asset-id={asset.id}
                  data-media-status={state?.status}
                  data-origin={state?.status === 'ready' ? state.origin ?? 'device' : undefined}
                  title={isViewOnly ? undefined : 'Drag onto the timeline to place it there; double-click to add it at the playhead'}
                  onDoubleClick={() => { if (!isViewOnly && !isMissing) commit(insertAsset(savedRef.current, asset.id, playback.getTime(), makeId)); }}
                  {...dragFromList({ kind: asset.kind, durationSec: asset.durationSec, source: { assetId: asset.id } })}
                >
                  <span className={`${thumbBox} ${isMissing ? 'bg-warn/20 text-lemon' : asset.kind === 'video' ? 'bg-lavender/25 text-lavender' : 'bg-mint/20 text-mint'}`}>
                    {thumbnail
                      ? <img src={thumbnail} alt="" draggable={false} className="w-full h-full object-cover" data-thumb />
                      : !state || state.status === 'loading' ? <Loader2 size={14} className="animate-spin" />
                      : isMissing ? <AlertTriangle size={14} />
                      : isWaiting ? <FolderLock size={14} />
                      : asset.kind === 'video' ? <Film size={14} /> : <AudioLines size={14} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={rowName} title={asset.name}>{asset.name}</span>
                    <span className="ns-t-meta block truncate" title={isMissing ? state.reason : about}>{about}</span>
                  </span>
                  {!isViewOnly && (
                    <span className="flex items-center shrink-0">
                      {isMissing ? (
                        <button type="button" onClick={() => { relinkTarget.current = asset.id; relinkInputRef.current?.click(); }} className={`${EDITOR_ICON_BTN} ns-icon-btn-xs text-lemon hover:text-lemon`} aria-label={`Relink ${asset.name}`} title="Relink: choose the file again">
                          <RefreshCw />
                        </button>
                      ) : (
                        <button type="button" onClick={() => commit(insertAsset(savedRef.current, asset.id, playback.getTime(), makeId))} className={`${EDITOR_ICON_BTN} ns-icon-btn-xs`} aria-label={`Add ${asset.name} to the timeline`} title="Add at the playhead, on the first free layer">
                          <Plus />
                        </button>
                      )}
                      <button type="button" onClick={() => dropAsset(asset)} className={EDITOR_REMOVE_BTN} aria-label={`Remove ${asset.name}`} title="Remove from the editor (its clips go too)">
                        <Trash2 />
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
            {canvasItems.length > 0 && (
              <li className="flex items-center gap-1 pl-2 pr-1.5 pt-2 pb-0.5">
                <span className="ns-t-label flex-1 truncate">{isPage ? 'In this workspace' : 'From the canvas'}</span>
                {!isViewOnly && canvasItems.length > 1 && (
                  <button
                    type="button"
                    onClick={() => void addFromCanvas(canvasItems)}
                    className={`${BTN_QUIET} px-2 py-0.5`}
                    title="Load all of these and add them to the timeline in this order"
                  >
                    <ListPlus size={14} /> Add all
                  </button>
                )}
              </li>
            )}
            {canvasItems.map(({ item, kind }) => (
              <li
                key={`${kind}-${item.id}`}
                className={`flex items-center gap-2 px-1.5 py-1.5 rounded-lg hover:bg-white/5 ${isViewOnly ? '' : 'cursor-grab active:cursor-grabbing'}`}
                data-canvas-item={item.id}
                title={isViewOnly ? undefined : 'Drag onto the timeline to place it there; double-click to add it at the playhead'}
                onDoubleClick={() => { if (!isViewOnly) void addFromCanvas([{ item, kind }], { timeSec: playback.getTime() }); }}
                // Its length is known once loaded; the drop preview guesses five seconds
                {...dragFromList({ kind, durationSec: 5, source: { item, kind } })}
              >
                <span className={`${thumbBox} bg-white/10 text-cream`}>
                  {kind === 'video' && thumbnails[poolThumb(item).key]
                    ? <img src={thumbnails[poolThumb(item).key]} alt="" draggable={false} className="w-full h-full object-cover" data-thumb />
                    : kind === 'video' ? <Film size={14} /> : <AudioLines size={14} />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={rowName} title={item.label}>{item.label}</span>
                  {(() => {
                    const id = canvasAssetId(kind, item);
                    if (loadingClip?.id === id) {
                      const megabytes = (bytes: number) => (bytes / 1024 / 1024).toFixed(1);
                      return (
                        <span className="block truncate text-2xs font-medium text-lavender" data-clip-progress>
                          {loadingClip.received > 0
                            ? `Downloading ${megabytes(loadingClip.received)}${loadingClip.total ? ` of ${megabytes(loadingClip.total)}` : ''} MB`
                            : 'Loading...'}
                        </span>
                      );
                    }
                    if (waitingClips.includes(id)) return <span className="ns-t-meta block truncate" data-clip-waiting>Waiting to be added</span>;
                    return item.note ? <span className="ns-t-meta block truncate" title={item.note}>{item.note}</span> : null;
                  })()}
                </span>
                {!isViewOnly && (
                  loadingClip?.id === canvasAssetId(kind, item)
                    ? <span className="w-6 h-6 shrink-0 grid place-items-center text-lavender" aria-label={`Adding ${item.label}`}><Loader2 size={12} className="animate-spin" /></span>
                    : (
                      <button
                        type="button"
                        onClick={() => void addFromCanvas([{ item, kind }])}
                        disabled={waitingClips.includes(canvasAssetId(kind, item))}
                        className={`${EDITOR_ICON_BTN} ns-icon-btn-xs`}
                        aria-label={`Add ${item.label} to the editor`}
                        title={busy ? 'Add to the timeline after the clip loading now' : 'Load and add to the timeline'}
                      >
                        <Plus />
                      </button>
                    )
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative flex-1 min-w-0 flex flex-col">
          <div className="relative flex-1 min-h-0 bg-black" data-editor-monitor>
            <canvas ref={canvasRef} className="w-full h-full block" />
            {saved.clips.length === 0 && (
              // The app's empty state, in the colours of a dark surface (the monitor stays black)
              <div className="ns-empty absolute inset-0 overflow-hidden pointer-events-none">
                <span className="ns-mark ns-mark-lg bg-white/10 text-cream/80"><Clapperboard aria-hidden="true" /></span>
                <p className="ns-empty-title text-cream">Nothing on the timeline yet</p>
                <p className="ns-empty-text text-ink-faint">
                  {isPage && canvasItems.length > 0
                    ? 'Drag this workspace\'s videos from the list onto the timeline, or drop files from this device on it. Each video brings its sound as a linked clip.'
                    : 'Drag videos from this device onto the timeline, or add them with the button above. Each video brings its sound as a linked clip.'}
                </p>
              </div>
            )}
          </div>

          {/* The transport, under the monitor it drives */}
          <div className="flex items-center gap-1 px-2 h-10 shrink-0 border-t border-white/10" data-editor-transport>
            <ToolButton label="Back to the start" shortcut="Home" onClick={() => playback.seek(0)} disabled={duration <= 0}><SkipBack /></ToolButton>
            <button
              type="button"
              onClick={playback.toggle}
              disabled={duration <= 0}
              aria-label={playback.isPlaying ? 'Pause' : 'Play'}
              title={`${playback.isPlaying ? 'Pause' : 'Play'} (Space)`}
              // The transport's main action: solid, and cream because the surface is ink
              className="ns-icon-btn ns-btn-cream focus-visible:outline-lavender"
            >
              {playback.isPlaying ? <Pause fill="currentColor" /> : <Play fill="currentColor" className="ml-0.5" />}
            </button>
            <ToolButton label="To the end" shortcut="End" onClick={() => playback.seek(duration)} disabled={duration <= 0}><SkipForward /></ToolButton>
            <span
              className={`font-mono text-2xs tabular-nums text-cream whitespace-nowrap ml-1.5 ${isPage ? 'min-w-[124px]' : ''}`}
              data-editor-timecode
            >
              {formatFrameTimecode(playback.timeSec, frameRate)} <span className="text-ink-faint">/ {formatFrameTimecode(duration, frameRate)}</span>
            </span>
            {isPage && (
              <span
                className="ns-chip ns-chip-sm ns-chip-dark shrink-0 tabular-nums whitespace-nowrap ml-1.5"
                title="The timeline runs at the frame rate of its clips. The timecode counts frames: minutes, seconds, frames."
                data-editor-fps
              >
                {Number.isInteger(frameRate) ? frameRate : frameRate.toFixed(3).replace(/0+$/, '')} fps
              </span>
            )}
            <div className="ml-auto flex items-center gap-0.5 shrink-0">
              {/* In and out points: the part that is exported */}
              <ToolButton label="Mark in at the playhead" shortcut="I" onClick={() => commit(setInPoint(savedRef.current, playback.getTime()))} disabled={isViewOnly || duration <= 0}>
                <ArrowRightToLine />
              </ToolButton>
              <ToolButton label="Mark out at the playhead" shortcut="O" onClick={() => commit(setOutPoint(savedRef.current, playback.getTime()))} disabled={isViewOnly || duration <= 0}>
                <ArrowLeftToLine />
              </ToolButton>
              {(saved.inSec !== undefined || saved.outSec !== undefined) && !isViewOnly && (
                <ToolButton label="Clear in and out" shortcut="Alt+X" onClick={() => commit(clearInOut(savedRef.current))}><X /></ToolButton>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* The editing tools, above the timeline they work on. (The light scrollbar is asked for by each dark
          box that scrolls, not by the whole editor: the export dialog inside a node's card is white.) */}
      <div className="ns-scroll-dark flex items-center gap-0.5 px-2 h-9 shrink-0 border-t border-white/10 overflow-x-auto" data-editor-tools>
        <ToolButton label="Select tool: Shift or Ctrl and a click adds clips, a drag across empty space selects several" shortcut="V" pressed={tool === 'select'} onClick={() => setTool('select')}><MousePointer2 /></ToolButton>
        <ToolButton label="Razor tool: click a clip to cut it" shortcut="C" pressed={tool === 'razor'} onClick={() => setTool('razor')} disabled={isViewOnly}><Slice /></ToolButton>
        <Divider />
        <ToolButton label="Cut at the playhead" shortcut="S" onClick={splitAtPlayhead} disabled={isViewOnly || saved.clips.length === 0}><Scissors /></ToolButton>
        <ToolButton label="Delete the selected clips" shortcut="Delete" onClick={deleteSelection} disabled={!anySelected}><Trash2 /></ToolButton>
        <ToolButton label="Ripple delete: delete the selected clips and close the gap" shortcut="Shift+Delete" onClick={rippleDeleteSelection} disabled={!anySelected}><Eraser /></ToolButton>
        <ToolButton label="Duplicate the selected clips, right after themselves" shortcut="Ctrl+D" onClick={duplicateSelection} disabled={!anySelected}><Copy /></ToolButton>
        <ToolButton
          label={linkAction === 'link' ? 'Link the selected clips' : linkAction === 'unlink' ? 'Unlink picture and sound' : 'Link or unlink: select clips first'}
          shortcut="L"
          onClick={toggleLink}
          disabled={!linkAction}
        >
          {linkAction === 'link' ? <Link2 /> : <Unlink2 />}
        </ToolButton>
        {selectedIds.length > 1 && (
          <span className="ns-t-meta px-1.5 shrink-0 tabular-nums whitespace-nowrap" title="Selected clips: Ctrl+A selects all, Esc clears" data-selection-count>
            {selectedIds.length} clips
          </span>
        )}
        {gainSelection.length > 0 && (
          // The selected clips' own volume: quieter than their track by this much
          <div className="flex items-center gap-1.5 pl-1.5 pr-1 shrink-0" title="Volume of the selected clips" data-clip-gain-control>
            <Volume2 size={14} className="text-cream/70 shrink-0" aria-hidden="true" />
            <div className="w-24">
              <MediaSlider
                value={1 - selectionGainDb / MIN_GAIN_DB}
                label="Clip volume"
                valueText={formatGainDb(selectionGainDb)}
                disabled={isViewOnly}
                step={1 / -MIN_GAIN_DB}
                onStart={() => { gainDrag.current = { base: savedRef.current, last: savedRef.current }; }}
                onChange={(fraction) => setSelectionGain(Math.round((fraction - 1) * -MIN_GAIN_DB * 2) / 2)}
                onEnd={() => {
                  const drag = gainDrag.current;
                  gainDrag.current = null;
                  if (drag && drag.last !== drag.base) commit(drag.last);
                  else setDraft(null);
                }}
                dataName="clip-gain"
              />
            </div>
            <span className="w-12 text-2xs font-semibold tabular-nums text-cream whitespace-nowrap" data-clip-gain-value>{formatGainDb(selectionGainDb)}</span>
            {selectionGainDb !== 0 && !isViewOnly && (
              <button type="button" onClick={() => commit(setClipGain(savedRef.current, selectedIds, 0))} className={`${BTN_QUIET} h-6 px-2 py-0`} title="Back to 0 dB">
                Reset
              </button>
            )}
          </div>
        )}
        <Divider />
        <ToolButton label="Snapping" pressed={saved.snapping} onClick={() => commit({ ...savedRef.current, snapping: !savedRef.current.snapping }, false)} disabled={isViewOnly}><Magnet /></ToolButton>
        <ToolButton label="Add a video track" onClick={() => commit(addTrack(savedRef.current, 'video', makeId))} disabled={isViewOnly}>
          <span className="relative"><Film size={16} /><Plus size={12} strokeWidth={3} className="absolute -right-2 -top-1.5" /></span>
        </ToolButton>
        <ToolButton label="Add an audio track" onClick={() => commit(addTrack(savedRef.current, 'audio', makeId))} disabled={isViewOnly}>
          <span className="relative"><AudioLines size={16} /><Plus size={12} strokeWidth={3} className="absolute -right-2 -top-1.5" /></span>
        </ToolButton>
        <div className="ml-auto flex items-center gap-0.5 pl-2 shrink-0">
          <ToolButton label="Zoom out" onClick={() => setZoom(saved.pxPerSec / 1.4)} disabled={saved.pxPerSec <= MIN_PX_PER_SEC}><ZoomOut /></ToolButton>
          <ToolButton label="Zoom in" onClick={() => setZoom(saved.pxPerSec * 1.4)} disabled={saved.pxPerSec >= MAX_PX_PER_SEC}><ZoomIn /></ToolButton>
          <ToolButton label="Fit the whole edit in view" shortcut="F" onClick={fitToView} disabled={duration <= 0}><Maximize2 /></ToolButton>
        </div>
      </div>

      <EditorTimeline
        ref={timelineRef}
        project={project}
        selectedIds={selectedIds}
        tool={tool}
        missingAssetIds={missingAssetIds}
        disabled={isViewOnly}
        getTime={playback.getTime}
        registerPlayhead={registerPlayhead}
        onSeek={playback.seek}
        frameRate={frameRate}
        onSelect={setSelectedIds}
        onPreview={setDraft}
        onCommit={commit}
        onCancelPreview={() => setDraft(null)}
        onZoom={setZoom}
        makeId={makeId}
        roomy={isPage}
        mediaDrag={mediaDrag}
        onDropMedia={dropMedia}
        onDropFiles={(files, drop) => { void addFiles(files, drop); }}
        waveforms={waveforms}
        waveformStatus={waveformStatus}
      />

      {exporting && (
        <ExportDialog variant={variant} project={saved} frameRate={frameRate} media={mediaBlobs} workspaceName={workspaceName} onClose={() => { setExporting(false); rootRef.current?.focus({ preventScroll: true }); }} />
      )}
    </div>
  );
};

export default QuickEditorNode;
